import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, KeyRound, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { generateId } from '../../utils/helpers';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';

const ROLES = ['admin', 'teacher', 'student', 'parent'];
const emptyForm = { name: '', email: '', password: '', role: 'admin', status: 'active' };
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

const roleColors = {
  admin:   'bg-purple-100 text-purple-700',
  teacher: 'bg-blue-100 text-blue-700',
  student: 'bg-green-100 text-green-700',
  parent:  'bg-orange-100 text-orange-700',
};

export default function UserManagement() {
  const { users, loadUsers, addUser, editUser, deleteUser, changePassword, currentUser, refreshCurrentUser } = useAuth();
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(emptyForm);
  const [selected, setSelected] = useState(null);
  const [pwForm, setPwForm]     = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError]   = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch]     = useState('');

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openAdd      = () => { setForm({ ...emptyForm }); setError(''); setModal('add'); };
  const openEdit     = (u) => { setForm({ ...u, password: '' }); setSelected(u); setError(''); setModal('edit'); };
  const openPassword = (u) => { setSelected(u); setPwForm({ current: '', next: '', confirm: '' }); setPwError(''); setModal('password'); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return setError('Name and email are required.');
    if (modal === 'add' && !form.password.trim()) return setError('Password is required.');
    setError(''); setSaving(true);
    try {
      if (modal === 'add') {
        await addUser({ ...form, id: generateId('USR'), profileId: '' });
      } else {
        const payload = { name: form.name, email: form.email, role: form.role, status: form.status };
        if (form.password.trim()) payload.password = form.password.trim();
        await editUser(selected.id, payload);
        if (selected.id === currentUser.id) refreshCurrentUser(payload);
      }
      setModal(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (user) => {
    if (user.id === currentUser.id) return alert('You cannot delete your own account.');
    if (!window.confirm(`Delete user "${user.name}"? This cannot be undone.`)) return;
    try {
      await deleteUser(user.id);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (pwForm.next.length < 6) return setPwError('New password must be at least 6 characters.');
    if (pwForm.next !== pwForm.confirm) return setPwError('Passwords do not match.');
    setSaving(true);
    try {
      await changePassword(selected.id, pwForm.current || null, pwForm.next);
      setModal(null);
    } catch (e) {
      setPwError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (search) {
      const q = search.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const roleCounts = ROLES.reduce((acc, r) => ({ ...acc, [r]: users.filter(u => u.role === r).length }), {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">User Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage system users and access roles</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
          <Plus size={16} /> Add User
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {ROLES.map(r => (
          <div key={r} className="bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
            <p className="text-2xl font-bold text-gray-800">{roleCounts[r]}</p>
            <p className={`text-xs font-semibold mt-1 capitalize inline-block px-2 py-0.5 rounded-full ${roleColors[r]}`}>{r}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 items-center">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 w-64" />
        <div className="flex gap-2">
          {['all', ...ROLES].map(r => (
            <button key={r} onClick={() => setFilterRole(r)}
              className={`px-3 py-1 rounded-lg text-sm font-medium border transition-colors capitalize ${filterRole === r ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              {['#', 'User', 'Email', 'Role', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-gray-400">No users found</td></tr>
            ) : filtered.map((u, i) => (
              <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: u.role === 'admin' ? '#7c3aed' : u.role === 'teacher' ? '#2563eb' : u.role === 'student' ? '#16a34a' : '#ea580c' }}>
                      {(u.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{u.name}</p>
                      {u.id === currentUser?.id && (
                        <span className="text-xs text-indigo-500 font-medium flex items-center gap-1">
                          <ShieldCheck size={11} /> You
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${roleColors[u.role]}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3"><Badge value={u.status || 'active'} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(u)} title="Edit"
                      className="p-1.5 text-gray-400 hover:text-indigo-600 rounded hover:bg-indigo-50"><Edit2 size={14} /></button>
                    <button onClick={() => openPassword(u)} title="Change Password"
                      className="p-1.5 text-gray-400 hover:text-yellow-600 rounded hover:bg-yellow-50"><KeyRound size={14} /></button>
                    <button onClick={() => remove(u)} title="Delete"
                      disabled={u.id === currentUser?.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal isOpen title={modal === 'add' ? 'Add New User' : 'Edit User'} onClose={() => setModal(null)}>
          <div className="space-y-4 p-1">
            <Field label="Full Name">
              <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} placeholder="e.g. Ramesh Sharma" />
            </Field>
            <Field label="Email Address">
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} placeholder="user@school.edu" />
            </Field>
            <Field label={modal === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)}
                className={inputCls} placeholder={modal === 'add' ? 'Min. 6 characters' : 'Leave blank to keep existing'} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Role">
                <select value={form.role} onChange={e => set('role', e.target.value)} className={inputCls}>
                  {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select value={form.status || 'active'} onChange={e => set('status', e.target.value)} className={inputCls}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </Field>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
              {saving ? 'Saving…' : modal === 'add' ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Change Password Modal */}
      {modal === 'password' && selected && (
        <Modal isOpen title={`Change Password — ${selected.name}`} onClose={() => setModal(null)}>
          <div className="space-y-4 p-1">
            {selected.id === currentUser?.id && (
              <Field label="Current Password">
                <input type="password" value={pwForm.current}
                  onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                  className={inputCls} placeholder="Enter your current password" />
              </Field>
            )}
            <Field label="New Password">
              <input type="password" value={pwForm.next}
                onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))}
                className={inputCls} placeholder="Min. 6 characters" />
            </Field>
            <Field label="Confirm New Password">
              <input type="password" value={pwForm.confirm}
                onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                className={inputCls} placeholder="Re-enter new password" />
            </Field>
            {pwError && <p className="text-red-500 text-sm">{pwError}</p>}
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button onClick={handleChangePassword} disabled={saving}
              className="px-5 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-60">
              {saving ? 'Saving…' : 'Update Password'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
